import type {
    FilteredValue,
    RepeatableRowExtraItem,
    RepeatableRowExtraView,
} from '../../interfaces/input.interface';

export interface ReactiveRepeatableMagramaMetadataEntry {
    readonly label: string;
    readonly value: string | null;
}

export interface ReactiveRepeatableMagramaExtraViewConfig {
    readonly styleClass?: string;
    readonly items: RepeatableRowExtraItem[];
}

/** Shared MAGRAMA row text/metadata helpers for repeatable controls. */
export class ReactiveRepeatableMagramaPatchPolicy {
    public static normalizeLookupText(value: unknown): string {
        return String(value ?? '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .replace(/\s+/g, ' ')
            .toLowerCase();
    }

    public static appendHydrationFallbackOption(
        options: FilteredValue<string>[],
        label: string,
    ): FilteredValue<string>[] {
        const normalizedLabel = this.normalizeLookupText(label);

        if (normalizedLabel === '') {
            return [...options];
        }

        const exists = options.some((option) => {
            const normalizedOptionValue = this.normalizeLookupText(option.value ?? option.label);
            const normalizedOptionLabel = this.normalizeLookupText(option.label);

            return normalizedOptionValue === normalizedLabel || normalizedOptionLabel === normalizedLabel;
        });

        if (exists) {
            return [...options];
        }

        return [...options, { label, value: label, raw: label, group: 'Hidratación' }];
    }

    public static resolveLegacyLabel(row: Record<string, unknown>, keys: readonly string[]): string {
        for (const key of keys) {
            const value = row[key];

            if (typeof value === 'string' || typeof value === 'number') {
                const normalizedValue = String(value).trim();

                if (normalizedValue !== '') {
                    return normalizedValue;
                }
            }
        }

        return '';
    }

    public static resolveLooseScopedKey<T>(source: Record<string, T>, selectedKey: string): string {
        if (selectedKey === '') {
            return '';
        }

        return Object.keys(source).find((key) => key === selectedKey)
            ?? Object.keys(source).find((key) => key.includes(selectedKey) || selectedKey.includes(key))
            ?? '';
    }

    public static resolveLooseScopedValue<T>(source: Record<string, T>, selectedKey: string, fallback: T): T {
        const key = this.resolveLooseScopedKey(source, selectedKey);

        return key !== '' ? source[key] ?? fallback : fallback;
    }

    public static findMetadataValue(entries: readonly ReactiveRepeatableMagramaMetadataEntry[], selectedValue: unknown): string {
        const normalizedSelectedValue = this.normalizeLookupText(selectedValue);
        const entry = entries.find((item) => this.normalizeLookupText(item.label) === normalizedSelectedValue);

        return String(entry?.value ?? '').trim();
    }

    public static extractSecurityTerm(doseRecommendation: string): string {
        const match = /plazo\s*seg\.?:?\s*([^,;]+)/iu.exec(doseRecommendation);
        const securityTerm = String(match?.[1] ?? '').trim();

        return securityTerm !== '' ? securityTerm : '';
    }

    public static extractDoseText(doseRecommendation: string): string {
        const normalized = doseRecommendation.trim();

        if (normalized === '') {
            return '';
        }

        return normalized
            .replace(/^dosis\.?:?\s*/iu, '')
            .replace(/\s*plazo\s*seg\.?:?.*$/iu, '')
            .trim();
    }

    public static getSelectedOptionLabel(options: FilteredValue<string>[], selectedValue: unknown): string {
        const normalizedValue = this.normalizeLookupText(selectedValue);

        if (normalizedValue === '') {
            return '';
        }

        const match = options.find((option) => this.normalizeLookupText(option.value ?? option.label) === normalizedValue);

        return match?.label ?? String(selectedValue ?? '').trim();
    }

    public static filterVisibleExtraItems(items: RepeatableRowExtraItem[]): RepeatableRowExtraItem[] {
        return items.filter((item) => item.value.trim() !== '');
    }

    public static createExtraView(config: ReactiveRepeatableMagramaExtraViewConfig): RepeatableRowExtraView {
        const items = this.filterVisibleExtraItems(config.items);

        return {
            isVisible: items.length > 0,
            ...(config.styleClass !== undefined ? { styleClass: config.styleClass } : {}),
            items,
        };
    }
}
